import { create } from 'zustand';
import { api } from '../../lib/api';
import { toast } from '../../lib/session';
import type { Media } from '../../lib/types';
import type { useLiveKit } from '../share/useLiveKit';

// The room you're in, like a call: you join it, and you stay in it (sync, voice, screen shares,
// chat alerts, the film in a mini player) while you browse the rest of the app, until you leave.
// Survives a reload of the tab (sessionStorage), not closing it.

const KEY = 'vb.activeRoom';
const saved = () => {
  try {
    return sessionStorage.getItem(KEY);
  } catch {
    return null;
  }
};
const save = (id: string | null) => {
  try {
    if (id) sessionStorage.setItem(KEY, id);
    else sessionStorage.removeItem(KEY);
  } catch {
    /* private mode */
  }
};

interface ActiveRoom {
  roomId: string | null;
  /** The room page's player slot; the docked player sits over it, or floats as a mini player. */
  anchor: HTMLElement | null;
  join: (id: string) => void;
  leave: () => void;
}

export const useActiveRoom = create<ActiveRoom>((set) => ({
  roomId: saved(),
  anchor: null,
  join: (id) => {
    save(id);
    set({ roomId: id });
  },
  leave: () => {
    save(null);
    set({ roomId: null });
  },
}));

/** Join a room and open it. */
export function joinRoom(id: string, nav: (to: string) => void) {
  useActiveRoom.getState().join(id);
  nav(`/room/${id}`);
}

/** Join the room already watching this title, or start one for it. */
export async function watchTogether(m: Media, nav: (to: string) => void) {
  try {
    const r = await api.watchTogether(m.id);
    if (r.joined) toast(`Joining the room already watching ${m.title}`, 'accent');
    joinRoom(r.room.id, nav);
  } catch (e) {
    toast((e as Error).message, 'red');
  }
}

export type LiveKit = ReturnType<typeof useLiveKit>;
/** The joined room's voice/screen-share connection, published by RoomSession for the room page. */
export const useRoomLive = create<{ lk: LiveKit | null }>(() => ({ lk: null }));
